/* Hydro Bottle Finder */
(() => {
  const DATA_EVENT = 'hydro-bottle-finder:data-ready';

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[char]);

  const parseSettings = (root) => {
    const node = root.querySelector('[data-hbf-settings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('Hydro Bottle Finder: invalid section settings JSON', error);
      return {};
    }
  };

  const initBottleFinder = (root) => {
    const data = window.HydroBottleFinderData;
    if (!root || !data || root.dataset.hbfInitialized === 'true') return;
    root.dataset.hbfInitialized = 'true';

    const sectionSettings = parseSettings(root);
    const settings = {
      maxResults: Number(sectionSettings.maxResults) || 12,
      showPrices: sectionSettings.showPrices !== false,
      showOutOfStock: sectionSettings.showOutOfStock !== false,
      buttonLabel: sectionSettings.buttonLabel || 'View product',
      rootUrl: sectionSettings.rootUrl || '/',
      searchUrl: sectionSettings.searchUrl || '/search',
      currency: sectionSettings.currency || 'PHP',
      locale: sectionSettings.locale || 'en-PH'
    };

    const state = { use: null, size: null, cap: null };
    const els = {
      panel: root.querySelector('[data-hbf-panel]'),
      stepLabel: root.querySelector('[data-hbf-step-label]'),
      title: root.querySelector('[data-hbf-question-title]'),
      description: root.querySelector('[data-hbf-question-description]'),
      options: root.querySelector('[data-hbf-options]'),
      back: root.querySelector('[data-hbf-back]'),
      reset: root.querySelector('[data-hbf-reset]'),
      results: root.querySelector('[data-hbf-results]'),
      resultsTitle: root.querySelector('[data-hbf-results-title]'),
      resultsSummary: root.querySelector('[data-hbf-results-summary]'),
      selectionSummary: root.querySelector('[data-hbf-selection-summary]'),
      productGrid: root.querySelector('[data-hbf-product-grid]'),
      resultsBack: root.querySelector('[data-hbf-results-back]'),
      resultsReset: root.querySelector('[data-hbf-results-reset]')
    };

    const normalizedRoot = () => String(settings.rootUrl || '/').replace(/\/?$/, '/');

    const buildRootedUrl = (path) => {
      const rawPath = String(path || '').trim();
      if (!rawPath) return '';
      if (/^https?:\/\//i.test(rawPath)) return rawPath;
      return `${normalizedRoot()}${rawPath.replace(/^\/+/, '')}`;
    };

    const getProductUrl = (product) => {
      if (product.url) {
        return buildRootedUrl(product.url);
      }
      if (product.handle) {
        return buildRootedUrl(`products/${encodeURIComponent(product.handle)}`);
      }
      const separator = String(settings.searchUrl || '/search').includes('?') ? '&' : '?';
      return `${settings.searchUrl || '/search'}${separator}q=${encodeURIComponent(product.sku)}`;
    };

    const formatPrice = (value) => {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return '';
      try {
        return new Intl.NumberFormat(settings.locale || undefined, {
          style: 'currency',
          currency: settings.currency || 'PHP'
        }).format(amount);
      } catch (_error) {
        return `${settings.currency || 'PHP'} ${amount.toLocaleString()}`;
      }
    };

    const getStep = () => {
      if (!state.use) return 'use';
      if (!state.size) return 'size';
      if (!state.cap) return 'cap';
      return 'results';
    };

    const findOption = (collection, id) => (collection || []).find((option) => option.id === id) || { label: id, description: '' };

    const getSelectedLabels = () => {
      const useOption = findOption(data.questions.use.options, state.use);
      const sizeOption = findOption(data.questions.size.optionsByUse[state.use], state.size);
      const capOption = findOption(data.questions.cap.optionsByUseAndSize?.[state.use]?.[state.size], state.cap);
      return { use: useOption.label, size: sizeOption.label, cap: capOption.label };
    };

    const pathKey = () => `${state.use}|${state.size}|${state.cap}`;

    const getProducts = () => {
      const skus = data.paths[pathKey()] || [];
      return skus
        .map((sku) => data.products[sku])
        .filter(Boolean)
        .filter((product) => settings.showOutOfStock || Number(product.inventory) > 0);
    };

    const updateProgress = (step) => {
      const order = ['use', 'size', 'cap', 'results'];
      const activeIndex = order.indexOf(step);
      root.querySelectorAll('[data-hbf-progress]').forEach((item) => {
        const itemStep = item.getAttribute('data-hbf-progress');
        const itemIndex = order.indexOf(itemStep);
        item.classList.toggle('is-active', itemStep === step);
        item.classList.toggle('is-complete', itemIndex < activeIndex);
      });
    };

    const renderOptions = (step, options) => {
  els.options.innerHTML = options.map((option) => {
    let count = '';
        if (step === 'cap') {
          const capPathKey = `${state.use}|${state.size}|${option.id}`;
          const productCount = (data.paths[capPathKey] || []).length;
          count = `<span class="hbf__option-count">${productCount} match${productCount === 1 ? '' : 'es'}</span>`;
        }
        return `
  <button type="button" class="hbf__option" data-hbf-select="${step}" data-hbf-value="${escapeHtml(option.id)}">

    ${option.image ? `
      <img
        class="hbf__option-image"
        src="${escapeHtml(option.image)}"
        alt="${escapeHtml(option.label)}"
        loading="lazy"
      >
    ` : ''}

    <span class="hbf__option-title">${escapeHtml(option.label)}</span>
    ${option.description ? `<span class="hbf__option-desc">${escapeHtml(option.description)}</span>` : ''}
    ${count}
  </button>`;
      }).join('');
    };

    const renderQuestion = (step) => {
      els.panel.hidden = false;
      els.results.hidden = true;
      els.reset.hidden = step === 'use';
      els.back.hidden = step === 'use';

      if (step === 'use') {
        els.stepLabel.textContent = 'Step 1 of 3';
        els.title.textContent = data.questions.use.label;
        els.description.textContent = data.questions.use.description || '';
        renderOptions('use', data.questions.use.options || []);
        return;
      }

      if (step === 'size') {
        els.stepLabel.textContent = 'Step 2 of 3';
        els.title.textContent = data.questions.size.label;
        els.description.textContent = data.questions.size.description || '';
        renderOptions('size', data.questions.size.optionsByUse[state.use] || []);
        return;
      }

      if (step === 'cap') {
        els.stepLabel.textContent = 'Step 3 of 3';
        els.title.textContent = data.questions.cap.label;
        els.description.textContent = data.questions.cap.description || '';
        renderOptions('cap', data.questions.cap.optionsByUseAndSize?.[state.use]?.[state.size] || []);
      }
    };

    const renderResults = () => {
      const labels = getSelectedLabels();
      const allProducts = getProducts();
      const visibleProducts = allProducts.slice(0, settings.maxResults);
      const totalPathProducts = (data.paths[pathKey()] || []).length;

      els.panel.hidden = true;
      els.results.hidden = false;
      els.resultsTitle.textContent = visibleProducts.length
        ? `We found ${allProducts.length} product${allProducts.length === 1 ? '' : 's'} for you.`
        : 'No products matched this exact combination.';
      els.resultsSummary.textContent = totalPathProducts > allProducts.length && !settings.showOutOfStock
        ? 'Some matched SKUs are hidden because out-of-stock products are disabled in this section.'
        : `Showing up to ${settings.maxResults} recommended product${settings.maxResults === 1 ? '' : 's'}.`;
      els.selectionSummary.innerHTML = `
        <span class="hbf__pill">${escapeHtml(labels.use)}</span>
        <span class="hbf__pill">${escapeHtml(labels.size)}</span>
        <span class="hbf__pill">${escapeHtml(labels.cap)}</span>`;

      if (!visibleProducts.length) {
        els.productGrid.innerHTML = '<div class="hbf__empty">Try a different cap, size, or use case to see more recommendations.</div>';
        return;
      }

      els.productGrid.innerHTML = visibleProducts.map((product) => {
        const inStock = Number(product.inventory) > 0;
        const price = settings.showPrices ? formatPrice(product.price) : '';
        const meta = [product.size, product.cap].filter(Boolean).join(' · ');
        const productUrl = getProductUrl(product);
        return `
          <a class="hbf__card hbf__card-link" href="${escapeHtml(productUrl)}" aria-label="${escapeHtml(product.title)}">
            <span class="hbf__image-wrap">
              ${product.image ? `<img class="hbf__image" src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="600" height="600">` : ''}
              <span class="hbf__badge">${inStock ? 'In stock' : 'Out of stock'}</span>
            </span>
            <span class="hbf__card-body">
              <span class="hbf__product-title">${escapeHtml(product.title)}</span>
              ${meta ? `<span class="hbf__meta">${escapeHtml(meta)}</span>` : ''}
              <span class="hbf__meta">SKU: ${escapeHtml(product.sku)}</span>
              ${price ? `<span class="hbf__price">${escapeHtml(price)}</span>` : ''}
              <span class="hbf__product-button">${escapeHtml(settings.buttonLabel)}</span>
            </span>
          </a>`;
      }).join('');
    };

    const render = () => {
      const step = getStep();
      updateProgress(step);
      if (step === 'results') {
    renderResults();
  } else {
        renderQuestion(step);
  }
};

const goBack = () => {
  const step = getStep();
  if (step === 'results') {
    state.cap = null;
      } else if (step === 'cap') {
    state.size = null;
      } else if (step === 'size') {
    state.use = null;
  }
  render();
};

    const reset = () => {
      state.use = null;
      state.size = null;
      state.cap = null;
      render();
    };

    root.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-hbf-select]');
      if (!choice || !root.contains(choice)) return;
      const step = choice.getAttribute('data-hbf-select');
      const value = choice.getAttribute('data-hbf-value');

      if (step === 'use') {
        state.use = value;
        state.size = null;
        state.cap = null;
      } else if (step === 'size') {
        state.size = value;
        state.cap = null;
      } else if (step === 'cap') {
        state.cap = value;
      }
      render();
    });

    [els.back, els.resultsBack].forEach((button) => button?.addEventListener('click', goBack));
    [els.reset, els.resultsReset].forEach((button) => button?.addEventListener('click', reset));

    render();
  };

  const initAll = () => {
    if (!window.HydroBottleFinderData) return;
    document.querySelectorAll('[data-hydro-bottle-finder]').forEach(initBottleFinder);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener(DATA_EVENT, initAll);
  document.addEventListener('shopify:section:load', initAll);
})();
